// The cities the world map covers. Each is its own server (path: data/<code>/
// on the page's host, see src/shared/places.ts), with its own lines (from the city's app, so they're the same as
// its own page's) and its network's extent. Ids are prefixed per region
// (namespace.ts).
//
// The page starts with what it needs to show and list them and to tell when
// one is near: a marker, a name, an extent. A city's lines, groups, credits
// and clock are its page config (London's, its own client's), a chunk of the
// build of its own that the page imports once the city is wanted
// (loadRegion): New York's thousands of buses aren't parsed on a phone
// looking at Berlin.

import { LngLatBounds } from 'maplibre-gl';
import type { LngLatBoundsLike } from 'maplibre-gl';
import type { GtfsPageConfig } from '@tol/gtfs/page';
import type { LineGroup } from '@tol/ui/shell';
import type { RosterLine } from '@tol/ui/roster';
import { dataPath, PLACES } from '../shared/places.ts';
import type { Place } from '../shared/places.ts';
import { prefixer } from './namespace.ts';
import type { Box } from './viewport.ts';
import { grow } from './viewport.ts';
import type { TitleArea } from './title-selection.ts';

// A line on the world page: the shell's, plus the bullet a GTFS city draws.
export interface WorldLine extends RosterLine {
  readonly bullet?: string;
}

interface RegionPlace {
  // Its code (places.ts): also the id prefix.
  readonly id: string;
  readonly name: string;
  // Its URL: transitof.com/<slug>.
  readonly place: Place;
  // Its server, relative to the world page: data/nyc/.
  readonly path: string;
  // The marker on the world view, [lon, lat].
  readonly center: readonly [number, number];
  // Every station is inside (test/regions.test.ts checks network.json).
  readonly extent: Box;
  // Geographic components rather than the envelope between disconnected
  // services. Bootstrap coverage only: known track can extend beyond it.
  readonly coverage?: readonly Box[];
  // Where its code is wanted, where that's more than its extent: London's
  // National Rail reaches the south coast (london-region.ts).
  readonly reach?: Box;
  // Its own zooms for loading and polling (viewport.ts RegionView).
  readonly loadZoom?: number;
  readonly pollZoom?: number;
  // Its world-map network is served as view-selected generation pieces.
  readonly partitioned?: boolean;
}

// A region before its code: all the page has of it from the start.
export type Region =
  | (RegionPlace & { readonly kind: 'gtfs'; readonly code: () => Promise<GtfsPageConfig> })
  | (RegionPlace & { readonly kind: 'london' });

// A region with its code (loadRegion).
interface RegionCommon extends RegionPlace {
  // Per line, in the region's own ids, where it differs by line (Paris).
  readonly keepRight: boolean | ((lineId: string) => boolean);
  // Prefixed.
  readonly lines: readonly WorldLine[];
  readonly groups: readonly LineGroup[];
  readonly accuracyGroups?: readonly LineGroup[];
  readonly credits: readonly string[];
  readonly pollMs: number;
  // Its clock, as its own page's.
  readonly timeZone: string;
  readonly locale: string;
}

export interface GtfsRegion extends RegionCommon {
  readonly kind: 'gtfs';
  readonly page: GtfsPageConfig;
}

export interface LondonRegion extends RegionCommon {
  readonly kind: 'london';
  readonly busPage: GtfsPageConfig;
}

export type LoadedRegion = GtfsRegion | LondonRegion;

export async function loadRegion(r: Region): Promise<LoadedRegion> {
  if (r.kind === 'gtfs') return gtfsRegion(r, await r.code());
  const { londonRegion } = await import('./london-region.ts');
  return londonRegion(r);
}

function gtfsRegion(r: RegionPlace, page: GtfsPageConfig): GtfsRegion {
  if (r.id === 'osl')
    page = {
      ...page,
      lines: page.lines.filter((l) => l.system !== 'train'),
      systems: page.systems.filter((s) => s.id !== 'train'),
    };
  const p = prefixer(r.id);
  return {
    ...r,
    kind: 'gtfs',
    keepRight: page.keepRight ?? false,
    lines: page.lines.map((l) => ({ ...l, id: p(l.id) })),
    groups: page.systems.map((s) => ({
      ...s,
      id: p(s.id),
      name: s.global === true ? s.name : `${r.name}: ${s.name}`,
      lines: page.lines.filter((l) => l.system === s.id).map((l) => p(l.id)),
    })),
    ...(page.accuracyGroups
      ? {
          accuracyGroups: page.accuracyGroups.map((g) => ({
            id: p(g.id),
            name: `${r.name}: ${g.name}`,
            lines: g.lines.map(p),
          })),
        }
      : {}),
    credits: page.credits.map((credit) =>
      credit.replaceAll(/href="(?![a-z]+:|\/|#)([^"]+)"/gu, (_match, path: string) => 'href="' + r.path + path + '"'),
    ),
    pollMs: page.pollMs ?? 15_000,
    timeZone: page.timeZone,
    locale: page.locale,
    page,
  };
}

function placeOf(code: string): Place {
  const place = PLACES.find((pl) => pl.code === code);
  if (!place) throw new TypeError(`no place ${code}`);
  return place;
}

function gtfs(
  id: string,
  name: string,
  center: readonly [number, number],
  extent: Box,
  code: () => Promise<GtfsPageConfig>,
): Region {
  return { kind: 'gtfs', id, name, place: placeOf(id), path: dataPath(id), center, extent, code };
}

const LONDON: Region = {
  kind: 'london',
  id: 'lon',
  name: 'London',
  place: placeOf('lon'),
  path: dataPath('lon'),
  center: [-0.1276, 51.5072],
  // Reading to Shenfield, Amersham to Morden.
  extent: [-1.05, 51.25, 0.4, 51.75],
  // National Rail's TD areas, from the south coast to beyond TfL's London.
  reach: [-6, 49.5, 2, 56],
};

// Where a click on a region's marker goes: its own page's opening view, a
// phone's on a phone (closer in: fewer trains to draw, as on the city's page).
export function openingView(r: LoadedRegion, phone: boolean): LngLatBoundsLike {
  if (r.kind === 'gtfs') return phone ? r.page.view.bounds.phone : r.page.view.bounds.desktop;
  return phone
    ? [
        [-0.15, 51.505],
        [-0.085, 51.52],
      ]
    : [
        [-0.165, 51.497],
        [-0.075, 51.53],
      ];
}

export function titleArea(r: LoadedRegion): TitleArea {
  const bounds = LngLatBounds.convert(openingView(r, false));
  bounds.extend(LngLatBounds.convert(openingView(r, true)));
  return {
    id: r.id,
    center: r.center,
    bounds: grow([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()], 1),
  };
}

// West to east, the order of the line list.
export const REGIONS: readonly Region[] = [
  gtfs('lugano', 'Lugano', [8.95, 46.0], [8.7, 45.8, 9.2, 46.3], () =>
    import('@tol/lugano/client/page').then((m) => m.LUGANO_PAGE),
  ),
  gtfs('lausanne', 'Lausanne', [6.63, 46.52], [6.25, 46.3, 6.95, 46.9], () =>
    import('@tol/lausanne/client/page').then((m) => m.LAUSANNE_PAGE),
  ),
  gtfs('geneva', 'Geneva', [6.14, 46.2], [5.7, 45.95, 6.6, 46.5], () =>
    import('@tol/geneva/client/page').then((m) => m.GENEVA_PAGE),
  ),
  gtfs('basel', 'Basel', [7.59, 47.56], [7.25, 47.25, 7.95, 47.8], () =>
    import('@tol/basel/client/page').then((m) => m.BASEL_PAGE),
  ),
  gtfs('zurich', 'Zurich', [8.54, 47.38], [8.1, 47.1, 8.95, 47.7], () =>
    import('@tol/zurich/client/page').then((m) => m.ZURICH_PAGE),
  ),
  gtfs('bern', 'Bern', [7.447, 46.948], [7.1, 46.65, 7.9, 47.2], () =>
    import('@tol/bern/client/page').then((m) => m.BERN_PAGE),
  ),
  {
    ...gtfs('gb', 'Great Britain', [-2.3, 54.1], [-6.5, 45.2, 8, 59], () =>
      import('./gb-page.ts').then((m) => m.GB_PAGE),
    ),
    path: dataPath('lon') + 'rail/',
    loadZoom: 4,
    pollZoom: 4,
  },
  {
    ...gtfs('db', 'Germany', [10.4, 51.1], [5.8, 47.2, 15.1, 55.2], () =>
      import('@tol/db/client/page').then((m) => m.DB_PAGE),
    ),
    loadZoom: 4,
    pollZoom: 4,
    partitioned: true,
  },
  {
    ...gtfs('ch', 'Switzerland', [8.2, 46.8], [5.99999, 45.83216, 10.28681, 47.74563], () =>
      import('@tol/ch/client/page').then((m) => m.CH_PAGE),
    ),
    loadZoom: 5,
    pollZoom: 5,
  },
  {
    ...gtfs('no', 'Norway', [10.4, 63], [4.5, 55.5, 20, 69], () =>
      import('@tol/no/client/page').then((m) => m.NO_PAGE),
    ),
    loadZoom: 3,
    pollZoom: 3,
  },
  {
    ...gtfs('se', 'Sweden', [15.2, 62], [9.1, 45.7, 24.2, 69.1], () =>
      import('@tol/se-national/client/page').then((m) => m.SE_PAGE),
    ),
    loadZoom: 3,
    pollZoom: 3,
  },
  {
    ...gtfs('mal', 'Skåne', [13, 55.6], [11.9, 55.3, 16.4, 57.8], () =>
      import('@tol/mal/client/page').then((m) => m.MAL_PAGE),
    ),
    loadZoom: 7,
    pollZoom: 7,
  },
  gtfs('tokyo', 'Tokyo', [139.745, 35.68], [138.50841, 34.048599, 141.02274, 37.92408], () =>
    import('@tol/tokyo/client/page').then((m) => m.TOKYO_PAGE),
  ),

  // WES reaches Wilsonville, MAX Hillsboro and Gresham.
  gtfs('pdx', 'Portland', [-122.68, 45.52], [-123.25, 45.2, -122.2, 45.75], () =>
    import('@tol/pdx/client/page').then((m) => m.PDX_PAGE),
  ),
  // Sounder reaches Everett and Lakewood, the T Line Tacoma.
  gtfs('sea', 'Seattle', [-122.33, 47.61], [-122.65, 47.05, -121.65, 48.1], () =>
    import('@tol/sea/client/page').then((m) => m.SEA_PAGE),
  ),
  gtfs('sfbay', 'Bay Area', [-122.27, 37.8], [-123.1, 36.8, -120.9, 39.2], () =>
    import('@tol/bay/client/page').then((m) => m.BAY_PAGE),
  ),
  // The buses reach Airdrie's edge and Chestermere's; the CTrain Tuscany to
  // Somerset and Saddletowne.
  gtfs('yyc', 'Calgary', [-114.07, 51.045], [-114.35, 50.8, -113.75, 51.25], () =>
    import('@tol/yyc/client/page').then((m) => m.YYC_PAGE),
  ),
  // The regional buses reach Boulder, Longmont and Nederland; the A Line the
  // airport.
  gtfs('den', 'Denver', [-104.99, 39.74], [-105.65, 39.4, -104.6, 40.27], () =>
    import('@tol/den/client/page').then((m) => m.DEN_PAGE),
  ),
  // Every Amtrak train: the continental US, and the Cascades to Vancouver,
  // the Adirondack to Montreal and the Maple Leaf to Toronto. Its marker is
  // the country's middle, in Kansas.
  // Seen whole, as its own page opens, with its trains: zoom 3.1 on a
  // 1024x768 laptop, 3.7 on a 1400x900 one (at 4, /amtrak opened on no
  // trains there; test/regions.test.ts).
  {
    ...gtfs('amtrak', 'Amtrak', [-98.58, 39.83], [-125, 24.5, -66.5, 49.6], () =>
      import('@tol/amtrak/client/page').then((m) => m.AMTRAK_PAGE),
    ),
    loadZoom: 3,
    pollZoom: 3,
  },
  // The express buses reach Hastings, Maple Grove and Shakopee.
  gtfs('msp', 'Minneapolis–St. Paul', [-93.2, 44.96], [-93.8, 44.65, -92.75, 45.4], () =>
    import('@tol/msp/client/page').then((m) => m.MSP_PAGE),
  ),
  // The 'L' reaches O'Hare, Linden in Wilmette, Dempster-Skokie and
  // 95th/Dan Ryan; the buses Evanston and 130th Street; Metra Kenosha,
  // Harvard, Big Timber, Elburn, Aurora, Joliet, Manhattan and University
  // Park.
  gtfs('chi', 'Chicago', [-87.63, 41.88], [-88.7, 41.35, -87.5, 42.65], () =>
    import('@tol/chi/client/page').then((m) => m.CHI_PAGE),
  ),
  // GO reaches London, Niagara Falls, Barrie and Oshawa.
  gtfs('tor', 'Toronto', [-79.38, 43.65], [-81.05, 42.9, -78.2, 44.45], () =>
    import('@tol/tor/client/page').then((m) => m.TOR_PAGE),
  ),
  // exo reaches Hudson, Saint-Jérôme, Mascouche, Mont-Saint-Hilaire and
  // Candiac.
  gtfs('mtl', 'Montréal', [-73.57, 45.5], [-74.4, 45.2, -72.9, 46.1], () =>
    import('@tol/mtl/client/page').then((m) => m.MTL_PAGE),
  ),
  // Regional Rail reaches Newark, Delaware, Thorndale, Doylestown and
  // Trenton, the buses Oxford Valley and Coatesville's edge.
  gtfs('phl', 'Philadelphia', [-75.16, 39.95], [-76.0, 39.55, -74.6, 40.45], () =>
    import('@tol/phl/client/page').then((m) => m.PHL_PAGE),
  ),
  // NJ Transit reaches Philadelphia and Atlantic City.
  gtfs('nyc', 'New York', [-73.98, 40.75], [-75.25, 39.3, -71.9, 41.85], () =>
    import('@tol/nyc/client/page').then((m) => m.NYC_PAGE),
  ),
  // Commuter Rail reaches Providence, Wickford Junction, Worcester and
  // Newburyport; CTrail New Haven, Stamford, Springfield and New London.
  gtfs('bos', 'Boston', [-71.06, 42.36], [-73.7, 40.95, -70.5, 42.9], () =>
    import('@tol/bos/client/page').then((m) => m.BOS_PAGE),
  ),
  LONDON,
  // Madrid's Cercanias run to Guadalajara, Aranjuez, El Escorial and
  // Cercedilla; Barcelona's Rodalies across Catalonia, to Puigcerda,
  // Figueres, Tortosa and Lleida.
  gtfs('mad', 'Madrid', [-3.7, 40.42], [-4.6, 39.6, -2.8, 41.2], () =>
    import('@tol/mad/client/page').then((m) => m.MAD_PAGE),
  ),
  gtfs('bcn', 'Barcelona', [2.17, 41.38], [-0.85, 40.4, 3.4, 42.7], () =>
    import('@tol/bcn/client/page').then((m) => m.BCN_PAGE),
  ),
  // Valencia's Cercanias run to Gandia, L'Alcúdia de Crespins, Buñol and
  // Vinaròs.
  gtfs('vlc', 'Valencia', [-0.38, 39.47], [-1.6, 37.9, 0.7, 40.7], () =>
    import('@tol/vlc/client/page').then((m) => m.VLC_PAGE),
  ),
  // Bilbao's Cercanias run to Santurtzi, Muskiz, Orduña, Karrantza and (the
  // narrow gauge, a few a day) León.
  gtfs('bio', 'Bilbao', [-2.93, 43.26], [-5.8, 42.4, -2.35, 43.5], () =>
    import('@tol/bio/client/page').then((m) => m.BIO_PAGE),
  ),
  // Seville's Cercanias run to Lora del Río, Lebrija, Cazalla-Constantina
  // and Benacazón.
  gtfs('svq', 'Seville', [-5.98, 37.39], [-6.4, 36.7, -5.3, 38.1], () =>
    import('@tol/svq/client/page').then((m) => m.SVQ_PAGE),
  ),
  // Asturias's Cercanias run from Oviedo and Gijón to Puente de los Fierros,
  // San Juan de Nieva, Cudillero, Laviana, Infiesto and Collanzo.
  gtfs('ovd', 'Asturias', [-5.85, 43.36], [-7.2, 42.9, -4.5, 43.7], () =>
    import('@tol/ovd/client/page').then((m) => m.OVD_PAGE),
  ),
  // Santander's run to Reinosa, Unquera, Liérganes and Gibaja.
  gtfs('sdr', 'Santander', [-3.81, 43.46], [-4.55, 42.95, -3.38, 43.5], () =>
    import('@tol/sdr/client/page').then((m) => m.SDR_PAGE),
  ),
  // San Sebastián's run from Irun up the Oria valley to Brinkola.
  gtfs('eas', 'San Sebastián', [-1.98, 43.32], [-3.0, 42.8, -1.7, 43.4], () =>
    import('@tol/eas/client/page').then((m) => m.EAS_PAGE),
  ),
  // Zaragoza's C1 runs from Casetas to Miraflores.
  gtfs('zaz', 'Zaragoza', [-0.89, 41.65], [-1.05, 41.6, -0.85, 41.74], () =>
    import('@tol/zaz/client/page').then((m) => m.ZAZ_PAGE),
  ),
  // Murcia and Alicante's run from Alicante to Murcia and Sant Vicent.
  gtfs('alc', 'Murcia and Alicante', [-0.49, 38.35], [-1.2, 37.9, -0.4, 38.45], () =>
    import('@tol/alc/client/page').then((m) => m.ALC_PAGE),
  ),
  // Málaga's run to Fuengirola and Álora.
  gtfs('agp', 'Málaga', [-4.43, 36.72], [-4.85, 36.45, -4, 37.05], () =>
    import('@tol/agp/client/page').then((m) => m.AGP_PAGE),
  ),
  // Cádiz's run to Jerez and its airport, and the T1 to Chiclana.
  gtfs('xry', 'Cádiz', [-6.29, 36.53], [-6.45, 36.1, -5.8, 36.8], () =>
    import('@tol/xry/client/page').then((m) => m.XRY_PAGE),
  ),
  // Lisbon's CP trains run to Sintra, Cascais and Azambuja, and from
  // Barreiro to Setúbal; Fertagus across the bridge to Setúbal.
  gtfs('lis', 'Lisbon', [-9.14, 38.72], [-9.5, 38.4, -8.4, 39.12], () =>
    import('@tol/lis/client/page').then((m) => m.LIS_PAGE),
  ),
  // Metromare reaches the sea at Ostia, Metro C Pantano, the buses
  // Fiumicino, and the Roma–Viterbo Viterbo.
  gtfs('rom', 'Rome', [12.5, 41.9], [11.3, 41.2, 14, 43.4], () =>
    import('@tol/rom/client/page').then((m) => m.ROM_PAGE),
  ),
  // The trains reach Vlissingen, Maastricht, Enschede and Groningen, and stop
  // at the border.
  gtfs('nl', 'Netherlands', [4.9, 52.37], [3.2, 50.7, 7.3, 53.6], () =>
    import('@tol/nl/client/page').then((m) => m.NL_PAGE),
  ),
  // The regional trains reach Rostock, Stralsund, Magdeburg, Leipzig,
  // Dresden and (the weekend Kulturzug) Wrocław.
  gtfs('ber', 'Berlin', [13.4, 52.52], [10.9, 50.8, 17.2, 54.5], () =>
    import('@tol/ber/client/page').then((m) => m.BER_PAGE),
  ),
  // Each German city's region is its gtfs.de box (apps/<code>/src/server/
  // city.ts), with a margin: the Rhine-Main S-Bahn to Wiesbaden, Mainz,
  // Darmstadt and Hanau; the HVV's to Stade and Bad Oldesloe; the MVV's to
  // Freising and Tutzing and the lakes; Stuttgart's to Bietigheim,
  // Pforzheim, Tübingen and Schorndorf; the S-Bahn Köln between Cologne
  // and Bonn; the Rhine-Ruhr's from Krefeld to Dortmund and Hagen;
  // Hannover's to Minden, Nienburg, Celle, Hildesheim and Hameln;
  // Nuremberg's to Forchheim, Neumarkt, Roth and Altdorf; Leipzig's to
  // Halle, Bitterfeld, Wurzen, Borna and Geithain; Dresden's to Meißen,
  // Freiberg and Bad Schandau.
  gtfs('fra', 'Frankfurt', [8.68, 50.11], [8.1, 49.75, 9.05, 50.45], () =>
    import('@tol/fra/client/page').then((m) => m.FRA_PAGE),
  ),
  gtfs('ham', 'Hamburg', [10.0, 53.55], [9.3, 53.25, 10.55, 53.9], () =>
    import('@tol/ham/client/page').then((m) => m.HAM_PAGE),
  ),
  gtfs('muc', 'Munich', [11.57, 48.14], [10.9, 47.75, 12.15, 48.5], () =>
    import('@tol/muc/client/page').then((m) => m.MUC_PAGE),
  ),
  gtfs('str', 'Stuttgart', [9.18, 48.78], [8.55, 48.43, 9.95, 49.05], () =>
    import('@tol/str/client/page').then((m) => m.STR_PAGE),
  ),
  gtfs('cgn', 'Cologne', [6.96, 50.94], [6.6, 50.5, 7.6, 51.25], () =>
    import('@tol/cgn/client/page').then((m) => m.CGN_PAGE),
  ),
  // Its opening view is 8.8 on a 1024x768 laptop: polled from there.
  {
    ...gtfs('ruhr', 'Rhine-Ruhr', [6.79, 51.22], [6.45, 51.05, 7.73, 51.67], () =>
      import('@tol/ruhr/client/page').then((m) => m.RUHR_PAGE),
    ),
    pollZoom: 8.5,
  },
  gtfs('han', 'Hannover', [9.74, 52.375], [8.75, 52.0, 10.25, 52.75], () =>
    import('@tol/han/client/page').then((m) => m.HAN_PAGE),
  ),
  gtfs('nue', 'Nuremberg', [11.08, 49.45], [10.6, 49.15, 11.6, 49.85], () =>
    import('@tol/nue/client/page').then((m) => m.NUE_PAGE),
  ),
  gtfs('lej', 'Leipzig', [12.375, 51.34], [11.75, 51.0, 12.95, 51.7], () =>
    import('@tol/lej/client/page').then((m) => m.LEJ_PAGE),
  ),
  gtfs('drs', 'Dresden', [13.74, 51.05], [13.1, 50.8, 14.45, 51.4], () =>
    import('@tol/drs/client/page').then((m) => m.DRS_PAGE),
  ),
  // The Rhine-Neckar's trams and S-Bahn reach Worms, Bensheim, Speyer,
  // Neustadt and Sinsheim.
  gtfs('rnk', 'Rhine-Neckar', [8.47, 49.487], [8.0, 49.15, 9.05, 49.78], () =>
    import('@tol/rnk/client/page').then((m) => m.RNK_PAGE),
  ),
  // Karlsruhe's Stadtbahn reaches Bruchsal, Bretten, Pforzheim, Bad
  // Wildbad, Baden-Baden and Wörth.
  gtfs('kar', 'Karlsruhe', [8.402, 49.009], [8.05, 48.62, 8.88, 49.3], () =>
    import('@tol/kar/client/page').then((m) => m.KAR_PAGE),
  ),
  // Bremen's Regio-S-Bahn reaches Bremerhaven, Nordenham, Oldenburg,
  // Verden and Twistringen.
  gtfs('bre', 'Bremen', [8.807, 53.079], [8.05, 52.68, 9.55, 53.68], () =>
    import('@tol/bre/client/page').then((m) => m.BRE_PAGE),
  ),
  // Freiburg's S-Bahn reaches Breisach, Elzach, Titisee-Neustadt and
  // Seebrugg.
  gtfs('fre', 'Freiburg', [7.848, 47.996], [7.4, 47.65, 8.35, 48.3], () =>
    import('@tol/fre/client/page').then((m) => m.FRE_PAGE),
  ),
  // Augsburg's regional trains reach Donauwörth, Dinkelscherben, Aichach
  // and Schwabmünchen.
  gtfs('agb', 'Augsburg', [10.894, 48.366], [10.45, 48.05, 11.3, 48.85], () =>
    import('@tol/agb/client/page').then((m) => m.AGB_PAGE),
  ),
  // The S-trains and regional trains reach Kolín, Mladá Boleslav, Rakovník,
  // Tábor and Strakonice.
  gtfs('prg', 'Prague', [14.42, 50.08], [13.2, 49.2, 15.8, 50.8], () =>
    import('@tol/prg/client/page').then((m) => m.PRG_PAGE),
  ),
  gtfs('vie', 'Vienna', [16.37, 48.21], [16.2, 48.08, 16.6, 48.32], () =>
    import('@tol/vie/client/page').then((m) => m.VIE_PAGE),
  ),
  // ZTM Gdańsk's buses reach Sopot, Pruszcz Gdański and Sobieszewo.
  gtfs('gdn', 'Gdańsk', [18.65, 54.35], [18.3, 54.2, 19.0, 54.5], () =>
    import('@tol/gdn/client/page').then((m) => m.GDN_PAGE),
  ),
  // MPK's buses reach Skawina, Wieliczka, Niepołomice and Zielonki.
  gtfs('krk', 'Kraków', [19.94, 50.06], [19.5, 49.85, 20.45, 50.3], () =>
    import('@tol/krk/client/page').then((m) => m.KRK_PAGE),
  ),
  // ZTM's buses reach Oborniki, Murowana Goślina, Kórnik, Mosina and Buk.
  gtfs('poz', 'Poznań', [16.93, 52.41], [16.35, 52.1, 17.4, 52.65], () =>
    import('@tol/poz/client/page').then((m) => m.POZ_PAGE),
  ),
  // The SKM reaches Otwock, Pruszków, Legionowo and Piaseczno; ZTM's buses
  // further.
  gtfs('waw', 'Warsaw', [21.01, 52.23], [20.5, 51.85, 21.55, 52.55], () =>
    import('@tol/waw/client/page').then((m) => m.WAW_PAGE),
  ),
  // ACTV's boats reach Chioggia, its buses Spinea and Pellestrina.
  gtfs('vce', 'Venice', [12.335, 45.438], [11.85, 45, 12.55, 45.7], () =>
    import('@tol/vce/client/page').then((m) => m.VCE_PAGE),
  ),
  gtfs('man', 'Manchester', [-2.24, 53.48], [-3.05, 53.2, -1.75, 53.9], () =>
    import('@tol/man/client/page').then((m) => m.MAN_PAGE),
  ),
  // Opens on Dublin; Irish Rail's InterCity lines reach Cork, Tralee,
  // Galway, Westport, Sligo and (the Enterprise) Belfast, NI Railways'
  // Derry~Londonderry, Portrush, Larne and Bangor.
  gtfs('dub', 'Ireland', [-6.26, 53.35], [-10.0, 51.7, -5.4, 55.3], () =>
    import('@tol/dub/client/page').then((m) => m.DUB_PAGE),
  ),
  gtfs('osl', 'Oslo', [10.75, 59.91], [5.3, 55.5, 13.2, 63.5], () =>
    import('@tol/osl/client/page').then((m) => m.OSL_PAGE),
  ),
  gtfs('hel', 'Helsinki', [24.94, 60.17], [23.8, 59.85, 25.9, 61.1], () =>
    import('@tol/hel/client/page').then((m) => m.HEL_PAGE),
  ),
  // Pendeltåg reaches Uppsala, Gnesta and Nynäshamn, Waxholmsbolaget's
  // boats the outer archipelago.
  gtfs('sto', 'Stockholm', [18.07, 59.33], [17.2, 58.7, 19.25, 60.25], () =>
    import('@tol/sto/client/page').then((m) => m.STO_PAGE),
  ),
  // The Västtåg trains reach Skövde, Borås, Varberg and Säffle.
  gtfs('got', 'Gothenburg', [11.97, 57.71], [11.5, 57.05, 15.3, 59.35], () =>
    import('@tol/got/client/page').then((m) => m.GOT_PAGE),
  ),
  gtfs('bud', 'Budapest', [19.04, 47.5], [18.65, 47.1, 19.45, 47.7], () =>
    import('@tol/bud/client/page').then((m) => m.BUD_PAGE),
  ),
  // All of Île-de-France, and the Transilien past it: Gisors, Dreux,
  // Montargis, Château-Thierry, Creil.
  gtfs('par', 'Paris', [2.35, 48.86], [1.3, 47.95, 3.6, 49.5], () =>
    import('@tol/par/client/page').then((m) => m.PAR_PAGE),
  ),
  // SNCF's national trains: France, and on to Geneva, Lausanne, Zurich,
  // Brussels, Frankfurt, Munich and Barcelona's edge. Seen whole, as its own
  // page opens, with its trains: zoom 5.2 on a 1024x768 laptop
  // (test/regions.test.ts).
  {
    ...gtfs('fr', 'France', [2.5, 46.6], [-61.7, -61.6, 166.8, 66.7], () =>
      import('@tol/fr/client/page').then((m) => m.FR_PAGE),
    ),
    // Bounds from the committed network's stations: mainland and European
    // cross-border services, then each disconnected overseas network. The
    // aggregate extent alone also encloses Japan and the oceans between.
    coverage: [
      // Europe, including international buses.
      [-9.5, 36, 36.5, 66.7],
      // Guadeloupe.
      [-61.7, 16.1, -61.1, 16.5],
      // Martinique.
      [-61.3, 14.3, -60.8, 14.9],
      // French Guiana.
      [-52.6, 4.4, -52, 5.2],
      // Mayotte.
      [45, -13, 45.3, -12.6],
      // Réunion.
      [55.2, -21.5, 55.9, -20.8],
      // New Caledonia.
      [166.3, -22.4, 166.8, -22.1],
    ],
    loadZoom: 5,
    pollZoom: 5,
    partitioned: true,
  },
  // The East Rail line reaches Lo Wu and Lok Ma Chau, Tung Chung the airport.
  gtfs('hk', 'Hong Kong', [114.17, 22.3], [113.85, 22.15, 114.4, 22.6], () =>
    import('@tol/hk/client/page').then((m) => m.HK_PAGE),
  ),
  // Transwa's trains reach Bunbury and (the Prospector) Kalgoorlie.
  gtfs('per', 'Perth', [115.86, -31.95], [115.5, -33.5, 121.6, -30.6], () =>
    import('@tol/per/client/page').then((m) => m.PER_PAGE),
  ),
  // The buses reach Victor Harbor and Mount Barker.
  gtfs('adl', 'Adelaide', [138.6, -34.93], [138.3, -35.45, 139.05, -34.45], () =>
    import('@tol/adl/client/page').then((m) => m.ADL_PAGE),
  ),
  // V/Line reaches Swan Hill, Albury, Bairnsdale and Warrnambool.
  gtfs('mel', 'Melbourne', [144.96, -37.81], [142.2, -38.7, 147.9, -35.0], () =>
    import('@tol/mel/client/page').then((m) => m.MEL_PAGE),
  ),
  // The light rail runs from Gungahlin Place to Alinga Street; the buses
  // reach Banks and Strathnairn.
  gtfs('cbr', 'Canberra', [149.13, -35.28], [148.95, -35.5, 149.3, -35.1], () =>
    import('@tol/cbr/client/page').then((m) => m.CBR_PAGE),
  ),
  // NSW TrainLink reaches Brisbane, Melbourne and (the Indian Pacific) Perth.
  gtfs('nsw', 'Sydney', [151.21, -33.87], [115.8, -37.9, 153.7, -27.4], () =>
    import('@tol/nsw/client/page').then((m) => m.NSW_PAGE),
  ),
  // The trains reach Gympie North and Varsity Lakes, G:link Burleigh Heads,
  // the buses Toowoomba's edge.
  gtfs('bne', 'Brisbane', [153.03, -27.47], [152.0, -28.3, 153.65, -26.0], () =>
    import('@tol/bne/client/page').then((m) => m.BNE_PAGE),
  ),
];
